import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/PasswordReset.css';

const PasswordReset = () => {
  const { resetPassword, isLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    try {
      await resetPassword(email);
      // Backend deliberately behaves the same whether or not the account
      // exists (no user enumeration) — always show the same success state.
      setSubmitted(true);
    } catch (error) {
      setLocalError(error.response?.data?.message || 'Something went wrong. Please try again.');
      console.error('Reset error:', error);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <>
      <Navbar />

      <motion.div
        className="password-reset-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="reset-container">
          {!submitted ? (
            <motion.div
              className="reset-content"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.h1 variants={itemVariants}>Reset Password</motion.h1>
              <motion.p variants={itemVariants} className="reset-subtitle">
                Enter your email address and we'll send you a link to reset your password.
              </motion.p>

              {localError && (
                <motion.div
                  className="error-alert"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <AlertCircle size={18} />
                  <span>{localError}</span>
                </motion.div>
              )}

              <motion.form onSubmit={handleSubmit} variants={containerVariants}>
                <motion.div className="form-group" variants={itemVariants}>
                  <label htmlFor="email">Email Address</label>
                  <div className="input-wrapper">
                    <Mail size={18} className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                    />
                  </div>
                </motion.div>

                <motion.button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isLoading}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                  {!isLoading && <ArrowRight size={18} />}
                </motion.button>

                <motion.div className="form-links" variants={itemVariants}>
                  <Link to="/signin" className="back-link">
                    <ArrowLeft size={16} />
                    Back to Sign In
                  </Link>
                </motion.div>
              </motion.form>
            </motion.div>
          ) : (
            <motion.div
              className="reset-success"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>

              <h2>Check Your Email</h2>
              <p>We've sent a password reset link to <strong>{email}</strong></p>
              <p className="small-text">Click the link in the email to reset your password. The link expires in 24 hours.</p>

              <motion.div
                className="success-actions"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.div variants={itemVariants}>
                  <Link to="/signin" className="btn btn-primary">
                    Back to Sign In
                  </Link>
                </motion.div>

                <motion.button
                  onClick={() => {
                    setSubmitted(false);
                    setEmail('');
                  }}
                  className="btn btn-outline"
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Try Another Email
                </motion.button>
              </motion.div>

              <motion.p className="resend-help" variants={itemVariants}>
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="resend-link"
                >
                  try again
                </button>
              </motion.p>
            </motion.div>
          )}
        </div>
      </motion.div>

      <Footer />
    </>
  );
};

export default PasswordReset;